import { PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH } from '@repo/auth-contracts';
import { DEFAULT_LOCALE, type Locale } from './locale';

/**
 * All user-facing copy lives here. Backend responses carry stable machine
 * `error` codes only — their text is NEVER rendered
 * (docs/auth/20-architecture/component-web-auth.md), so a backend message can
 * never leak internals or become an injection vector.
 */
export interface Copy {
  brandTagline: string;
  emailTitle: string;
  emailSubtitle: string;
  emailLabel: string;
  emailPlaceholder: string;
  orDivider: string;
  emailSubmit: string;
  signupTitle: string;
  signupSubtitle: string;
  signupSwitchToSignIn: string;
  signInSwitchToSignup: string;
  codeTitle: string;
  codeSubtitle: (email: string) => string;
  codeLabel: string;
  codeHint: (length: number) => string;
  codeSubmit: string;
  codeResend: string;
  codeResendIn: (seconds: number) => string;
  codeResent: string;
  signupGivenName: string;
  signupFamilyName: string;
  birthdateLabel: string;
  birthdateHint: (minimumAge: number) => string;
  birthdateRequiredTitle: string;
  birthdateRequiredSubtitle: string;
  birthdateRequiredSubmit: string;
  termsRequiredTitle: string;
  termsRequiredSubtitle: string;
  /** The label around the Terms of Use link; the link text is `legalTerms`. */
  termsCheckboxBefore: string;
  termsCheckboxAfter: string;
  /** The label around the Privacy Policy link; link text is `legalPrivacy`. */
  privacyCheckboxBefore: string;
  privacyCheckboxAfter: string;
  termsRequiredSubmit: string;
  termsRequiredUnchecked: string;
  profileTitle: string;
  profileSubtitle: (email: string) => string;
  profileSubmit: string;
  passwordLabel: string;
  passwordSubmit: string;
  passwordTitle: string;
  passwordSubtitle: string;
  usePasswordInstead: string;
  useCodeInstead: string;
  passwordShow: string;
  passwordHide: string;
  passwordOptionalLabel: string;
  passwordAddOptional: string;
  passwordNewLabel: string;
  passwordHint: string;
  passwordSetTitle: string;
  passwordSetSubtitle: string;
  passwordSetSubmit: string;
  passwordSkip: string;
  forgotLink: string;
  forgotTitle: string;
  forgotSubtitle: string;
  forgotSubmit: string;
  forgotSentTitle: string;
  forgotSentBody: (email: string, ttlMinutes: number) => string;
  resetTitle: string;
  resetSubtitle: string;
  resetSubmit: string;
  resetDoneTitle: string;
  resetDoneBody: string;
  socialDivider: string;
  socialContinueWith: (provider: string) => string;
  socialPendingTitle: string;
  socialPendingBody: (reason: string) => string;
  socialPendingContinue: string;
  passkeyCta: string;
  passkeyOfferTitle: string;
  passkeyOfferSubtitle: string;
  passkeyOfferSubmit: string;
  passkeyOfferSkip: string;
  consentTitle: string;
  consentSubtitle: (clientName: string) => string;
  consentSubtitleMore: (clientName: string) => string;
  consentAllow: string;
  consentDeny: string;
  consentScopesLabel: string;
  consentCapabilitiesLabel: string;
  consentNewScopesLabel: string;
  consentNewCapabilitiesLabel: string;
  consentAlreadyGrantedLabel: string;
  finishing: string;
  errorTitle: string;
  errorRetry: string;
  back: string;
  returnToApp: string;
  backToSignIn: string;
  languageSelector: string;
  legalNotice: string;
  legalPrivacy: string;
  legalTerms: string;
  scopeDescriptions: Record<string, string>;
}

const EN: Copy = {
  brandTagline: 'Buds Rewards',
  emailTitle: 'Sign in',
  emailSubtitle: 'Enter your email and we will send you a one-time code.',
  emailLabel: 'Email address',
  emailPlaceholder: 'you@example.com',
  orDivider: 'or',
  emailSubmit: 'Send code',
  signupTitle: 'Create your account',
  // Says the quiet part out loud on purpose. An existing address gets signed
  // in rather than told it exists — the server cannot behave differently
  // without leaking who has an account — so the wording promises exactly that
  // up front instead of letting it feel like a bug (ADR 0019).
  signupSubtitle:
    "Enter your email and we'll send you a code. If you already have an account, we'll just sign you in.",
  signupSwitchToSignIn: 'Already have an account? Sign in',
  signInSwitchToSignup: 'New to Buds? Create an account',
  codeTitle: 'Enter your code',
  codeSubtitle: (email) => `We sent a 6-digit code to ${email}.`,
  codeLabel: 'One-time code',
  codeHint: (length) =>
    `Submits automatically once all ${length} digits are entered.`,
  codeSubmit: 'Sign in',
  codeResend: 'Send a new code',
  codeResendIn: (seconds) => `Send a new code in ${seconds}s`,
  codeResent: 'A new code is on its way.',
  signupGivenName: 'First name',
  signupFamilyName: 'Last name',
  birthdateLabel: 'Date of birth',
  birthdateHint: (minimumAge) =>
    `You must be at least ${minimumAge} to use Buds Rewards.`,
  birthdateRequiredTitle: 'One more thing',
  birthdateRequiredSubtitle:
    'We need your date of birth before you can continue. You only have to do this once.',
  birthdateRequiredSubmit: 'Continue',
  termsRequiredTitle: 'Before you continue',
  termsRequiredSubtitle:
    'Please review and accept these to use Buds Rewards. They open in a new tab.',
  termsCheckboxBefore: 'I accept the ',
  termsCheckboxAfter: '',
  privacyCheckboxBefore: 'I have read the ',
  privacyCheckboxAfter: '',
  termsRequiredSubmit: 'Accept and continue',
  termsRequiredUnchecked: 'Both need to be accepted before you can continue.',
  profileTitle: 'A few details',
  // Names the address: this is the account-creation decision point, and the
  // user should not have to remember which of their addresses they proved.
  profileSubtitle: (email) =>
    email
      ? `${email} is confirmed. Tell us who you are to finish setting up your account.`
      : 'Your email is confirmed. Tell us who you are to finish setting up your account.',
  profileSubmit: 'Create account',
  passwordLabel: 'Password',
  passwordSubmit: 'Sign in',
  passwordTitle: 'Sign in with your password',
  passwordSubtitle: 'Use this when you cannot get to your email right now.',
  usePasswordInstead: 'Use password instead',
  useCodeInstead: 'Email me a code instead',
  passwordShow: 'Show',
  passwordHide: 'Hide',
  passwordOptionalLabel: 'Password (optional)',
  passwordAddOptional: 'Add a password (optional)',
  passwordNewLabel: 'New password',
  passwordHint: `At least ${PASSWORD_MIN_LENGTH} characters. Length matters more than symbols.`,
  passwordSetTitle: 'Add a password?',
  passwordSetSubtitle:
    'A password lets you sign in on the days your email is out of reach. You can skip this.',
  passwordSetSubmit: 'Save password',
  passwordSkip: 'Not now',
  forgotLink: 'Forgot your password?',
  forgotTitle: 'Reset your password',
  forgotSubtitle:
    'Enter your email address and we will send you a link to set a new one.',
  forgotSubmit: 'Send reset link',
  forgotSentTitle: 'Check your email',
  // Deliberately non-committal: saying "we sent it" would confirm the address
  // belongs to an account (docs/auth/40-features/password-reset.md AC1).
  forgotSentBody: (email: string, ttlMinutes: number) =>
    `If ${email} belongs to an account, a link to set a new password is on its way. It expires in ${ttlMinutes} minutes.`,
  resetTitle: 'Set a new password',
  resetSubtitle: 'Choose a new password for your account.',
  resetSubmit: 'Save password',
  resetDoneTitle: 'All done here',
  resetDoneBody:
    'If that link was still valid, your password has been changed and you have been signed out everywhere. Head back to the app and sign in.',
  socialDivider: 'or',
  socialContinueWith: (provider: string) => `Continue with ${provider}`,
  socialPendingTitle: 'One more step',
  // Each reason gets its own sentence: "something went wrong" would be both
  // useless and untrue — nothing went wrong, we just will not guess.
  socialPendingBody: (reason: string) =>
    ({
      no_email:
        "That provider didn't share an email address with us, so we need one to finish setting up your account.",
      email_unverified:
        "That provider didn't confirm the email address belongs to you, so we'll confirm it ourselves.",
      ambiguous_email:
        'More than one account uses that email address. Confirm it and we will sign you into the right one.',
      email_untrusted:
        'We have that email address on file but have never confirmed it ourselves. Confirm it and we will connect the two.',
    })[reason] ??
    'We need to confirm your email address before connecting that account.',
  socialPendingContinue: 'Continue with email',
  passkeyCta: 'Continue with a passkey',
  passkeyOfferTitle: 'Set up a passkey?',
  passkeyOfferSubtitle:
    'Use your fingerprint, face, or screen lock to sign in next time — nothing to remember, and phishing-resistant by design.',
  passkeyOfferSubmit: 'Set up passkey',
  passkeyOfferSkip: 'Not now',
  consentTitle: 'Allow access',
  consentSubtitle: (clientName) =>
    `${clientName} wants to access your Buds Rewards account.`,
  consentSubtitleMore: (clientName) =>
    `${clientName} is asking for more access to your Buds Rewards account.`,
  consentAllow: 'Allow',
  consentDeny: 'Deny',
  consentScopesLabel: 'It will be able to see:',
  consentCapabilitiesLabel: 'It will also be able to:',
  consentNewScopesLabel: 'New — it will also be able to see:',
  consentNewCapabilitiesLabel: 'New — it will also be able to:',
  consentAlreadyGrantedLabel: 'You allowed this earlier:',
  finishing: 'Signing you in…',
  errorTitle: 'Something went wrong',
  errorRetry: 'Try again',
  back: 'Use a different email',
  returnToApp: 'Cancel and go back to the app',
  backToSignIn: 'Back to sign-in',
  languageSelector: 'Language',
  legalNotice: 'Site Notice',
  legalPrivacy: 'Privacy Policy',
  legalTerms: 'Terms of Use',
  scopeDescriptions: {
    openid: 'That you are signed in to Buds Rewards',
    profile: 'Your name, profile picture, language and time zone',
    email: 'Your email address and whether it is verified',
    offline_access: 'Keep access while you are away',
    birthdate: 'Your exact date of birth',
    age: 'Whether you are over 18 and over 21 (not your date of birth)',
  },
};

const DE: Copy = {
  brandTagline: 'Buds Rewards',
  emailTitle: 'Anmelden',
  emailSubtitle: 'Gib deine E-Mail-Adresse ein — wir senden dir einen Code.',
  emailLabel: 'E-Mail-Adresse',
  emailPlaceholder: 'du@beispiel.de',
  orDivider: 'oder',
  emailSubmit: 'Code senden',
  signupTitle: 'Konto erstellen',
  signupSubtitle:
    'Gib deine E-Mail-Adresse ein — wir senden dir einen Code. Falls du schon ein Konto hast, melden wir dich einfach an.',
  signupSwitchToSignIn: 'Du hast schon ein Konto? Anmelden',
  signInSwitchToSignup: 'Neu bei Buds? Konto erstellen',
  codeTitle: 'Code eingeben',
  codeSubtitle: (email) =>
    `Wir haben einen 6-stelligen Code an ${email} gesendet.`,
  codeLabel: 'Einmal-Code',
  codeHint: (length) =>
    `Wird automatisch abgeschickt, sobald alle ${length} Ziffern eingegeben sind.`,
  codeSubmit: 'Anmelden',
  codeResend: 'Neuen Code senden',
  codeResendIn: (seconds) => `Neuen Code senden in ${seconds}s`,
  codeResent: 'Ein neuer Code ist unterwegs.',
  signupGivenName: 'Vorname',
  signupFamilyName: 'Nachname',
  birthdateLabel: 'Geburtsdatum',
  birthdateHint: (minimumAge) =>
    `Du musst mindestens ${minimumAge} Jahre alt sein, um Buds Rewards zu nutzen.`,
  birthdateRequiredTitle: 'Noch eine Sache',
  birthdateRequiredSubtitle:
    'Wir brauchen dein Geburtsdatum, bevor es weitergeht. Das ist nur einmal nötig.',
  birthdateRequiredSubmit: 'Weiter',
  termsRequiredTitle: 'Bevor es weitergeht',
  termsRequiredSubtitle:
    'Bitte lies und akzeptiere diese, um Buds Rewards zu nutzen. Sie öffnen in einem neuen Tab.',
  termsCheckboxBefore: 'Ich akzeptiere die ',
  termsCheckboxAfter: '',
  privacyCheckboxBefore: 'Ich habe die ',
  privacyCheckboxAfter: ' gelesen',
  termsRequiredSubmit: 'Akzeptieren und fortfahren',
  termsRequiredUnchecked:
    'Beides muss akzeptiert werden, bevor es weitergehen kann.',
  profileTitle: 'Noch ein paar Angaben',
  profileSubtitle: (email) =>
    email
      ? `${email} ist bestätigt. Sag uns noch, wer du bist, um dein Konto einzurichten.`
      : 'Deine E-Mail-Adresse ist bestätigt. Sag uns noch, wer du bist, um dein Konto einzurichten.',
  profileSubmit: 'Konto erstellen',
  passwordLabel: 'Passwort',
  passwordSubmit: 'Anmelden',
  passwordTitle: 'Mit Passwort anmelden',
  passwordSubtitle: 'Nutze das, wenn du gerade nicht an deine E-Mails kommst.',
  usePasswordInstead: 'Stattdessen Passwort verwenden',
  useCodeInstead: 'Stattdessen Code per E-Mail',
  passwordShow: 'Anzeigen',
  passwordHide: 'Verbergen',
  passwordOptionalLabel: 'Passwort (optional)',
  passwordAddOptional: 'Passwort hinzufügen (optional)',
  passwordNewLabel: 'Neues Passwort',
  passwordHint: `Mindestens ${PASSWORD_MIN_LENGTH} Zeichen. Länge zählt mehr als Sonderzeichen.`,
  passwordSetTitle: 'Passwort hinzufügen?',
  passwordSetSubtitle:
    'Mit einem Passwort kommst du auch dann rein, wenn deine E-Mails gerade nicht erreichbar sind. Du kannst das überspringen.',
  passwordSetSubmit: 'Passwort speichern',
  passwordSkip: 'Jetzt nicht',
  forgotLink: 'Passwort vergessen?',
  forgotTitle: 'Passwort zurücksetzen',
  forgotSubtitle:
    'Gib deine E-Mail-Adresse ein — wir schicken dir einen Link für ein neues Passwort.',
  forgotSubmit: 'Link senden',
  forgotSentTitle: 'Schau in dein Postfach',
  forgotSentBody: (email: string, ttlMinutes: number) =>
    `Falls ${email} zu einem Konto gehört, ist ein Link für ein neues Passwort unterwegs. Er läuft in ${ttlMinutes} Minuten ab.`,
  resetTitle: 'Neues Passwort setzen',
  resetSubtitle: 'Wähle ein neues Passwort für dein Konto.',
  resetSubmit: 'Passwort speichern',
  resetDoneTitle: 'Fertig',
  resetDoneBody:
    'War der Link noch gültig, wurde dein Passwort geändert und du wurdest überall abgemeldet. Geh zurück zur App und melde dich an.',
  socialDivider: 'oder',
  socialContinueWith: (provider: string) => `Weiter mit ${provider}`,
  socialPendingTitle: 'Noch ein Schritt',
  socialPendingBody: (reason: string) =>
    ({
      no_email:
        'Dieser Anbieter hat uns keine E-Mail-Adresse übermittelt. Für dein Konto brauchen wir eine.',
      email_unverified:
        'Dieser Anbieter hat nicht bestätigt, dass die E-Mail-Adresse dir gehört — das holen wir jetzt nach.',
      ambiguous_email:
        'Mehr als ein Konto nutzt diese E-Mail-Adresse. Bestätige sie, dann melden wir dich beim richtigen an.',
      email_untrusted:
        'Diese E-Mail-Adresse haben wir gespeichert, aber nie selbst bestätigt. Bestätige sie, dann verbinden wir beides.',
    })[reason] ??
    'Wir müssen deine E-Mail-Adresse bestätigen, bevor wir das Konto verbinden.',
  socialPendingContinue: 'Weiter mit E-Mail',
  passkeyCta: 'Weiter mit Passkey',
  passkeyOfferTitle: 'Passkey einrichten?',
  passkeyOfferSubtitle:
    'Melde dich künftig mit Fingerabdruck, Gesicht oder Displaysperre an — nichts zu merken, und durch sein Design resistent gegen Phishing.',
  passkeyOfferSubmit: 'Passkey einrichten',
  passkeyOfferSkip: 'Jetzt nicht',
  consentTitle: 'Zugriff erlauben',
  consentSubtitle: (clientName) =>
    `${clientName} möchte auf dein Buds-Rewards-Konto zugreifen.`,
  consentSubtitleMore: (clientName) =>
    `${clientName} möchte zusätzlichen Zugriff auf dein Buds-Rewards-Konto.`,
  consentAllow: 'Erlauben',
  consentDeny: 'Ablehnen',
  consentScopesLabel: 'Folgendes wird sichtbar:',
  consentCapabilitiesLabel: 'Außerdem ist Folgendes erlaubt:',
  consentNewScopesLabel: 'Neu — zusätzlich wird Folgendes sichtbar:',
  consentNewCapabilitiesLabel: 'Neu — zusätzlich ist Folgendes erlaubt:',
  consentAlreadyGrantedLabel: 'Das hast du bereits erlaubt:',
  finishing: 'Du wirst angemeldet …',
  errorTitle: 'Etwas ist schiefgelaufen',
  errorRetry: 'Erneut versuchen',
  back: 'Andere E-Mail-Adresse verwenden',
  returnToApp: 'Abbrechen und zur App zurückkehren',
  backToSignIn: 'Zurück zur Anmeldung',
  languageSelector: 'Sprache',
  legalNotice: 'Impressum',
  legalPrivacy: 'Datenschutz',
  legalTerms: 'Nutzungsbedingungen',
  scopeDescriptions: {
    openid: 'Dass du bei Buds Rewards angemeldet bist',
    profile: 'Dein Name, dein Profilbild, deine Sprache und Zeitzone',
    email: 'Deine E-Mail-Adresse und ob sie bestätigt ist',
    offline_access: 'Zugriff behalten, während du abwesend bist',
    birthdate: 'Dein genaues Geburtsdatum',
    age: 'Ob du über 18 und über 21 bist (nicht dein Geburtsdatum)',
  },
};

const COPY: Record<Locale, Copy> = { en: EN, de: DE };

export function copyFor(locale: Locale): Copy {
  return COPY[locale] ?? COPY[DEFAULT_LOCALE];
}

/** Error-code → human copy. Unknown codes fall back to a generic message. */
/** Exported for the parity spec only — rendering goes through messageFor. */
export const ERROR_MESSAGES: Record<Locale, Record<string, string>> = {
  en: {
    invalid_code: 'That code is not valid. Request a new one and try again.',
    terms_not_accepted:
      'The Terms of Use and Privacy Policy both need to be accepted before you can continue.',
    invalid_credentials:
      'That email and password combination did not work. Try again, or ask for a code by email.',
    invalid_password: `That password cannot be used. It must be ${PASSWORD_MIN_LENGTH} to ${PASSWORD_MAX_LENGTH} characters long.`,
    invalid_email: 'That does not look like an email address.',
    social_failed:
      'That sign-in could not be completed. Try again, or use your email address.',
    invalid_passkey:
      'That passkey did not work. Try again, or sign in with your email address.',
    passkey_cancelled:
      "Passkey sign-in didn't finish. If you don't have one yet, use your email below — you can add a passkey straight after.",
    // Reached from sign-up AND from the age gate at login (ADR 0021), so the
    // wording must be true of both: at sign-up no account is created, at login
    // an existing one is simply refused.
    under_minimum_age: 'You are not old enough to use Buds Rewards.',
    invalid_request: 'Please check your input and try again.',
    interaction_not_found:
      'This sign-in link has expired. Start again from the app.',
    csrf: 'Your session expired. Reload the page and try again.',
    rate_limited: 'Too many attempts. Please wait a moment and try again.',
    network: 'We could not reach Buds Rewards. Check your connection.',
    unknown: 'Something went wrong. Please try again.',
  },
  de: {
    invalid_code:
      'Dieser Code ist ungültig. Fordere einen neuen an und versuche es erneut.',
    terms_not_accepted:
      'Nutzungsbedingungen und Datenschutzerklärung müssen beide akzeptiert werden, bevor es weitergehen kann.',
    invalid_credentials:
      'E-Mail-Adresse und Passwort passen nicht zusammen. Versuche es erneut oder lass dir einen Code schicken.',
    invalid_password: `Dieses Passwort geht nicht. Es muss ${PASSWORD_MIN_LENGTH} bis ${PASSWORD_MAX_LENGTH} Zeichen lang sein.`,
    invalid_email: 'Das sieht nicht nach einer E-Mail-Adresse aus.',
    social_failed:
      'Diese Anmeldung konnte nicht abgeschlossen werden. Versuche es erneut oder nutze deine E-Mail-Adresse.',
    invalid_passkey:
      'Dieser Passkey hat nicht funktioniert. Versuche es erneut oder melde dich mit deiner E-Mail-Adresse an.',
    passkey_cancelled:
      'Die Passkey-Anmeldung wurde nicht abgeschlossen. Falls du noch keinen hast, nutze unten deine E-Mail-Adresse — einen Passkey kannst du direkt danach einrichten.',
    under_minimum_age: 'Du bist noch nicht alt genug für Buds Rewards.',
    invalid_request: 'Bitte überprüfe deine Eingabe und versuche es erneut.',
    interaction_not_found:
      'Dieser Anmeldelink ist abgelaufen. Starte erneut in der App.',
    csrf: 'Deine Sitzung ist abgelaufen. Lade die Seite neu.',
    rate_limited:
      'Zu viele Versuche. Bitte warte einen Moment und versuche es erneut.',
    network: 'Buds Rewards ist nicht erreichbar. Prüfe deine Verbindung.',
    unknown: 'Etwas ist schiefgelaufen. Bitte versuche es erneut.',
  },
};

export function messageFor(code: string, locale: Locale): string {
  const table = ERROR_MESSAGES[locale] ?? ERROR_MESSAGES[DEFAULT_LOCALE];
  return table[code] ?? table.unknown!;
}

/** Human description for an OIDC scope shown on the consent screen. */
export function scopeDescription(scope: string, locale: Locale): string {
  return copyFor(locale).scopeDescriptions[scope] ?? scope;
}
